import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import {
  DeviceCompatibilityState,
  DistributionFormType,
  FileUploadFormType,
  ManifestInformationFormType,
  PackageMetadataFormType,
  ReleaseNoteFormType,
  StepErrorsType,
  StepperFormStateType,
  StepValidationStateType
} from '../../entities/models/firmware-stepper.model';
import { extractVersionFromXmlContent } from '../../helpers/extractVersionFromXmlContent';
import { STEP_CONFIG, StepId } from '../../infrastructure/types/firmware-stepper.types';
import { FrontendFirmwarePackageDetails } from '../../infrastructure/types/firmware-upload.types';

const initialState: StepperFormStateType = {
  currentSection: StepId.FileUpload,
  stepOrder: STEP_CONFIG.map(s => s.id),
  menuItems: STEP_CONFIG,
  selectedFiles: [],
  fileUpload: { fileName: '', fileSize: 0, fileType: '', file: null, isValid: false },
  manifestInfo: { manifestName: '', isValid: false },
  packageMetadata: { classification: '', packageName: '', isValid: false },
  deviceCompatibility: { compatible_device_software_versions: [], isValid: false },
  distribution: { access: '', supportedCompanionApps: [], isValid: false },
  releaseNotes: { release_notes: '', isValid: false },
  stepValidation: {
    fileUpload: false,
    manifestInfo: false,
    packageMetadata: false,
    deviceCompatibility: false,
    distribution: false,
    releaseNotes: false,
    summary: false
  },
  errors: {},
  processing: false,
  uploadedManifestVersion: '',
  showCompatibilityError: false,
  IsIdenticalOrExceedingVersion: false,
  versionIdMismatch: false,
  isEditingFirmwarePackage: false,
  firmwarePackageDataForEdit: {
    packageMetadata: { classification: '', packageName: '' },
    distribution: {
      access: '',
      supportedCompanionApps: []
    },
    deviceCompatibility: { compatible_device_software_versions: [] },
    fileUpload: { fileName: '', fileSize: 0 },
    manifestInfo: { manifest: '' },
    releaseNotes: { notes: {} },
    packageStatus: ''
  },
  isCompanionAppVersionEmpty: false,
  hasInvalidCompanionAppVersion: false,
  isBelowGeneralMinVersion: false
};

export const StepperFormStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods(store => ({
    // Navigation
    setCurrentSection(section: string) {
      patchState(store, { currentSection: section });
    },
    canNavigateToStep: () => (stepId: string) => {
      const validation = store.stepValidation() || {};
      const order = store.stepOrder() || [];
      const currentIndex = order.indexOf(store.currentSection());
      const targetIndex = order.indexOf(stepId);
      if (targetIndex === -1) return false;
      if (targetIndex <= currentIndex) return true;
      return order.slice(0, targetIndex).every(s => validation[s as keyof typeof validation]);
    },
    goToNextStep() {
      const index = store.stepOrder().indexOf(store.currentSection());
      if (index < store.stepOrder().length - 1) {
        patchState(store, { currentSection: store.stepOrder()[index + 1] });
      }
    },

    //  File Upload
    addFiles(files: File[]) {
      patchState(store, state => ({ selectedFiles: [...state.selectedFiles, ...files] }));
    },
    removeFile(index: number) {
      patchState(store, state => ({
        selectedFiles: state.selectedFiles.filter((_, i) => i !== index)
      }));
    },
    clearFiles() {
      patchState(store, { selectedFiles: [] });
    },
    updateFileUpload(fields: Partial<FileUploadFormType>, error?: string) {
      patchState(store, state => ({
        fileUpload: { ...state.fileUpload, ...fields },
        stepValidation: {
          ...state.stepValidation,
          fileUpload: fields.isValid ?? state.fileUpload.isValid
        },
        errors: { ...state.errors, fileUpload: error ?? '' }
      }));
    },

    //  Manifest Info
    updateManifestInfo(fields: Partial<ManifestInformationFormType>, error?: string) {
      patchState(store, state => ({
        manifestInfo: { ...state.manifestInfo, ...fields },
        stepValidation: {
          ...state.stepValidation,
          manifestInfo: fields.isValid ?? state.manifestInfo.isValid
        },
        errors: { ...state.errors, manifestInfo: error ?? '' }
      }));
    },

    //  Attributes
    updatePackageMetadata(fields: Partial<PackageMetadataFormType>, error?: string) {
      patchState(store, state => ({
        packageMetadata: { ...state.packageMetadata, ...fields },
        stepValidation: {
          ...state.stepValidation,
          packageMetadata: fields.isValid ?? state.packageMetadata.isValid
        },
        errors: { ...state.errors, packageMetadata: error ?? '' }
      }));
    },
    // Device Compatibility
    updateDeviceCompatibility(fields: Partial<DeviceCompatibilityState>, error?: string) {
      patchState(store, state => ({
        deviceCompatibility: { ...state.deviceCompatibility, ...fields },
        stepValidation: {
          ...state.stepValidation,
          deviceCompatibility: fields.isValid ?? state.deviceCompatibility.isValid
        },
        errors: { ...state.errors, deviceCompatibility: error ?? '' }
      }));
    },

    updateDeviceCompatibilityArray(inputId: number, version: string) {
      patchState(store, state => {
        const existingIndex =
          state.deviceCompatibility.compatible_device_software_versions.findIndex(
            v => v.id === inputId
          );

        let updated;
        if (existingIndex > -1) {
          updated = [...state.deviceCompatibility.compatible_device_software_versions];
          updated[existingIndex] = { id: inputId, value: version };
        } else {
          updated = [
            ...state.deviceCompatibility.compatible_device_software_versions,
            { id: inputId, value: version }
          ];
        }

        return {
          deviceCompatibility: {
            ...state.deviceCompatibility,
            compatible_device_software_versions: updated
          }
        };
      });
    },

    removeCompatibleVersionById(inputId: number) {
      patchState(store, state => {
        const updated = state.deviceCompatibility.compatible_device_software_versions.filter(
          version => version.id !== inputId
        );
        return {
          deviceCompatibility: {
            ...state.deviceCompatibility,
            compatible_device_software_versions: updated
          }
        };
      });
    },

    // distribution
    updateDistribution(fields: Partial<DistributionFormType>, error?: string) {
      patchState(store, state => ({
        distribution: { ...state.distribution, ...fields },
        stepValidation: {
          ...state.stepValidation,
          distribution: fields.isValid ?? state.distribution.isValid
        },
        errors: { ...state.errors, distribution: error ?? '' }
      }));
    },

    // release notes
    setReleaseNotesValue(fields: ReleaseNoteFormType, error?: string) {
      patchState(store, state => ({
        releaseNotes: {
          release_notes: fields.release_notes,
          isValid: fields.isValid
        },
        stepValidation: {
          ...state.stepValidation,
          releaseNotes: fields.isValid
        },
        errors: {
          ...state.errors,
          releaseNotes: error ?? ''
        }
      }));
    },

    //  Summary & Validation
    updateSummary(isValid: boolean) {
      patchState(store, state => ({
        stepValidation: { ...state.stepValidation, summary: isValid }
      }));
    },

    // update the uploaded manifest version
    setUploadedManifestVersion(version: string) {
      patchState(store, { uploadedManifestVersion: version });
    },

    // show compatibility error
    setShowCompatibilityError(value: boolean) {
      patchState(store, { showCompatibilityError: value });
    },

    //  set IsIdenticalOrExceedingVersion flag
    setIsIdenticalOrExceedingVersion(value: boolean) {
      patchState(store, { IsIdenticalOrExceedingVersion: value });
    },

    // set invalid companion app version flag
    setHasInvalidCompanionAppVersion(value: boolean) {
      patchState(store, { hasInvalidCompanionAppVersion: value });
    },

    // set below general min version flag
    setIsBelowGeneralMinVersion(value: boolean) {
      patchState(store, { isBelowGeneralMinVersion: value });
    },

    // set below when the inputs are empty.
    setIsCompanionAppVersionEmpty(value: boolean) {
      patchState(store, { isCompanionAppVersionEmpty: value });
    },
    // set versionIdMismatch flag
      setHasVersionIdMismatch(value: boolean) {
      patchState(store, { versionIdMismatch: value });
    },

    // reset all state to initial state.
    resetStore() {
      patchState(store, initialState);
    },
    updateStepValidation(step: keyof StepValidationStateType, isValid: boolean) {
      patchState(store, state => ({
        stepValidation: { ...state.stepValidation, [step]: isValid }
      }));
    },

    //  Errors
    setError(step: keyof StepErrorsType, message: string) {
      patchState(store, state => ({ errors: { ...state.errors, [step]: message } }));
    },
    clearError(step: keyof StepErrorsType) {
      patchState(store, state => {
        const errors = { ...state.errors };
        delete errors[step];
        return { errors };
      });
    },

    //Processing
    setProcessing(value: boolean) {
      patchState(store, { processing: value });
    },

    // set edit mode for the firmware package.
    setEditingFirmwarePackge(value: boolean) {
      patchState(store, { isEditingFirmwarePackage: value });
    },

    // Load firmware data into store for editing
    editFirmwarePackage(packageDetails: FrontendFirmwarePackageDetails) {
      const releaseNotesObject = packageDetails.releaseNotes.notes || {};
      const releaseNotesString = JSON.stringify({ notes: releaseNotesObject }, null, 2);

      // Extract version from manifest XML
      const manifestVersion = packageDetails.manifestInfo.manifest 
        ? extractVersionFromXmlContent(packageDetails.manifestInfo.manifest)
        : '';

      patchState(store, () => ({
        fileUpload: {
          fileName: packageDetails.fileUpload.fileName,
          fileSize: packageDetails.fileUpload.fileSize,
          fileType: '',
          file: null,
          isValid: true
        },
        manifestInfo: {
          manifestName: packageDetails.manifestInfo.manifest,
          isValid: true
        },
        packageMetadata: {
          classification: packageDetails.packageMetadata.classification,
          packageName: packageDetails.packageMetadata.packageName,
          isValid: true
        },
        distribution: {
          access: packageDetails.distribution.access,
          supportedCompanionApps: packageDetails.distribution.supportedCompanionApps.map(app => ({
            name: app.name,
            appleMinimumVersion: app.appleMinimumVersion,
            androidMinimumVersion: app.androidMinimumVersion
          })),
          isValid: true
        },
        deviceCompatibility: {
          compatible_device_software_versions:
            packageDetails.deviceCompatibility.compatible_device_software_versions.map(
              (version, index) => ({
                id: index,
                value: version
              })
            ),
          isValid: true
        },
        releaseNotes: {
          release_notes: releaseNotesString,
          isValid: true
        },
        uploadedManifestVersion: manifestVersion,
        isEditingFirmwarePackage: true
      }));
    }
  })),

  //  Computed
  withComputed(state => ({
    canNavigateNext: () => {
      const current = state.currentSection();
      const order = state.stepOrder();
      const validation = state.stepValidation();
      return (
        current === order[order.length - 1] || !!validation[current as keyof typeof validation]
      );
    },

    isStepValid: () => (stepId: string) =>
      state.stepValidation()[stepId as keyof StepValidationStateType] || false,

    isStepDone: () => (stepId: string) => {
      const stepsOrder = state.stepOrder();
      const activeStepIndex = state.stepOrder().indexOf(state.currentSection());
      const stepIndex = stepsOrder.indexOf(stepId);
      if (activeStepIndex > stepIndex) return true;
      return false;
    },

    isFormDirty: () => {
      const validation = state.stepValidation();
      return Object.values(validation).some(isValid => isValid);
    },

    getAllFormData: () => ({
      fileUpload: state.fileUpload(),
      manifestInfo: state.manifestInfo(),
      packageMetadata: state.packageMetadata(),
      distribution: state.distribution(),
      releaseNotes: state.releaseNotes(),
      deviceCompatibility: state.deviceCompatibility()
    }),

    getManifestVersion: () => ({
      version: state.uploadedManifestVersion()
    }),

    getError: () => (step: keyof StepErrorsType) => state.errors()[step] ?? ''
  }))
);
